import { FlagProvider, useUnleashContext } from '@unleash/proxy-client-react'
import { useUser } from '@clerk/clerk-react'
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { unleashConfig } from '../config/unleash'
import { buildUnleashContext } from '@/lib/unleashContext'

const UnleashUserContextReadyContext = createContext(false)

// Upper bound on how long flag-gated routes wait for the context-aware flag
// re-fetch. updateContext never rejects on fetch failures (the client
// swallows them) and can hang indefinitely when the proxy is unreachable
// before the client's first successful fetch, so readiness must not depend
// solely on it settling.
const USER_CONTEXT_READY_TIMEOUT_MS = 5000

/**
 * Whether the Clerk user/org identifiers have been pushed into the Unleash
 * context and the resulting flag re-fetch completed. Flags evaluated before
 * this point can be spuriously false for user-sticky rollouts
 * (stickiness=userId), because the initial fetch carries no userId.
 */
export function useUnleashUserContextReady(): boolean {
  return useContext(UnleashUserContextReadyContext)
}

type UpdaterProps = {
  readonly onReady: () => void
}

/**
 * Inner component that pushes the user's Unleash context when the user changes.
 * This must be inside the FlagProvider to use useUnleashContext
 */
function UnleashContextUpdater({ onReady }: UpdaterProps) {
  const { user, isLoaded } = useUser()
  const updateContext = useUnleashContext()

  useEffect(() => {
    // Signed-out visitors have no identifiers to push; flags are as ready as
    // they are going to get.
    if (isLoaded && !user) {
      onReady()
      return
    }
    if (!user) return

    const context = buildUnleashContext(user)

    let cancelled = false
    // Update the Unleash context with custom properties. updateContext
    // re-fetches flags with the new identifiers; only once it resolves are
    // user-sticky rollouts evaluated correctly. Race it against a timeout so
    // an unreachable Unleash proxy degrades to the pre-fix behavior (flags
    // may evaluate false) instead of leaving flag-gated routes blank forever.
    const timeoutId = setTimeout(() => {
      if (!cancelled) onReady()
    }, USER_CONTEXT_READY_TIMEOUT_MS)
    updateContext(context)
      .catch(() => {
        // Settle readiness regardless; the guard evaluates with whatever
        // flags the client has rather than hanging.
      })
      .finally(() => {
        if (!cancelled) onReady()
      })
    return () => {
      cancelled = true
      clearTimeout(timeoutId)
    }
  }, [user, isLoaded, updateContext, onReady])

  return null
}

type UnleashProviderProps = {
  readonly children: ReactNode
}

/**
 * Wrapper around Unleash FlagProvider that automatically updates context
 * with organization information from Clerk
 */
export default function UnleashProvider({ children }: UnleashProviderProps) {
  const [isUserContextReady, setIsUserContextReady] = useState(false)
  const handleReady = useCallback(() => setIsUserContextReady(true), [])

  return (
    <FlagProvider config={unleashConfig}>
      <UnleashUserContextReadyContext.Provider value={isUserContextReady}>
        <UnleashContextUpdater onReady={handleReady} />
        {children}
      </UnleashUserContextReadyContext.Provider>
    </FlagProvider>
  )
}
